/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { CompoundRule, Rule } from './rule';

import type { CompoundCondition, Condition, Conditions, Facts, InputConditions } from './index';
import type { RuleNode } from './rule';

declare const __LIB_VERSION__: string;
const logger = Logger.getInstance();

class Engine {
  conditions: Conditions;
  inputConditions: InputConditions;
  private _normalised: boolean = false;

  constructor(conditions: InputConditions = {}) {
    this.conditions = {};
    this.inputConditions = conditions;
  }

  public normaliseConditions(): void {
    if (this._normalised) {
      return;
    }
    for (const key in this.inputConditions) {
      if (this.inputConditions.hasOwnProperty(key)) {
        const conditionKey = key as keyof InputConditions;
        this.conditions[conditionKey] = this.inputConditions[conditionKey]!.map((rule) => {
          if (rule instanceof Rule || rule instanceof CompoundRule) {
            return rule;
          }
          return this.convertToRuleNode(rule);
        });
      }
    }
    this._normalised = true;
  }

  public run(facts: Facts): boolean {
    logger.debug(
      `Running rule for conditions: ${JSON.stringify(this.inputConditions)} against facts: ${JSON.stringify(facts)}`
    );
    this.normaliseConditions();

    const anyConditionSatisfied = this.conditions.ANY
      ? this.conditions.ANY.some((rule) => this.evaluateRuleNode(rule, facts))
      : true;
    const allConditionSatisfied = this.conditions.ALL
      ? this.conditions.ALL.every((rule) => this.evaluateRuleNode(rule, facts))
      : true;
    const noneConditionSatisfied = this.conditions.NONE
      ? !this.conditions.NONE.some((rule) => this.evaluateRuleNode(rule, facts))
      : true;

    return anyConditionSatisfied && allConditionSatisfied && noneConditionSatisfied;
  }

  public static mergeConditions(
    baseConditions: InputConditions,
    additionalConditions: InputConditions
  ): InputConditions {
    const merged: InputConditions = { ...baseConditions };

    (Object.keys(additionalConditions) as Array<keyof InputConditions>).forEach((key) => {
      if (additionalConditions[key]) {
        merged[key] = merged[key]
          ? [...merged[key]!, ...additionalConditions[key]!]
          : [...additionalConditions[key]!];
      }
    });

    return merged;
  }

  private convertToRuleNode(rule: Condition | CompoundCondition): RuleNode {
    if (this.isCompoundCondition(rule)) {
      const childRules = rule.rules.map((childRule) => this.convertToRuleNode(childRule));
      return new CompoundRule(rule.op, childRules);
    } else {
      return new Rule(rule.fact, rule.op, rule.val);
    }
  }

  private isCompoundCondition(rule: Condition | CompoundCondition): rule is CompoundCondition {
    return 'op' in rule && 'rules' in rule && !('fact' in rule) && !('val' in rule);
  }

  private evaluateRuleNode(rule: RuleNode, facts: Facts): boolean {
    if (rule instanceof Rule) {
      return rule.evaluate(facts[rule.fact]);
    } else if (rule instanceof CompoundRule) {
      return rule.evaluate(facts);
    }
    throw new Error('Invalid rule type');
  }
}

export { Engine };
